/**
 * NFC 管理页面
 * NFC Management Page
 *
 * NFC 身份注册管理、扫描日志查看、绑定规则查询
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Tag,
  Tabs,
  Card,
  Row,
  Col,
  Statistic,
  InputNumber,
  Divider,
  Breadcrumb,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ReloadOutlined,
  RobotOutlined,
  ScanOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type {
  NfcIdentityRegistry,
  NfcIdentityRegistryCreate,
  NfcIdentityRegistryUpdate,
  NfcScanLog,
  NfcScanStatistics,
  NfcSource,
  ScanSource,
  EntityType,
  NfcStatus,
  ScanResult,
} from '../services/nfcService';
import {
  getIdentityList,
  createIdentity,
  updateIdentity,
  deleteIdentity,
  retireIdentity,
  restoreIdentity,
  getScanLogList,
  createScanLog,
  getScanStatistics,
  getNfcBindings,
  getNfcRequirements,
  ENTITY_TYPE_LABELS,
  NFC_STATUS_LABELS,
  NFC_SOURCE_LABELS,
  SCAN_RESULT_LABELS,
  SCAN_SOURCE_LABELS,
} from '../services/nfcService';

const { TextArea } = Input;
const { Option } = Select;

const NfcManagement: React.FC = () => {
  // ============================================================
  // 状态管理 - 身份注册
  // ============================================================
  const [identities, setIdentities] = useState<NfcIdentityRegistry[]>([]);
  const [identityLoading, setIdentityLoading] = useState(false);
  const [identityTotal, setIdentityTotal] = useState(0);
  const [identityPage, setIdentityPage] = useState(1);
  const [identityPageSize, setIdentityPageSize] = useState(20);
  const [entityTypeFilter, setEntityTypeFilter] = useState<EntityType | undefined>();
  const [statusFilter, setStatusFilter] = useState<NfcStatus | undefined>();
  const [searchKeyword, setSearchKeyword] = useState<string>('');

  // 模态框状态
  const [identityModalVisible, setIdentityModalVisible] = useState(false);
  const [editingIdentity, setEditingIdentity] = useState<NfcIdentityRegistry | null>(null);
  const [identityForm] = Form.useForm();

  // ============================================================
  // 状态管理 - 扫描日志
  // ============================================================
  const [scanLogs, setScanLogs] = useState<NfcScanLog[]>([]);
  const [scanLogLoading, setScanLogLoading] = useState(false);
  const [scanLogTotal, setScanLogTotal] = useState(0);
  const [scanLogPage, setScanLogPage] = useState(1);
  const [scanLogPageSize, setScanLogPageSize] = useState(20);
  const [scanResultFilter, setScanResultFilter] = useState<ScanResult | undefined>();
  const [worldIdFilter, setWorldIdFilter] = useState<string | undefined>();

  // 扫描统计
  const [statistics, setStatistics] = useState<NfcScanStatistics | null>(null);

  // 创建扫描日志模态框
  const [scanLogModalVisible, setScanLogModalVisible] = useState(false);
  const [scanLogForm] = Form.useForm();

  // ============================================================
  // 状态管理 - 绑定规则
  // ============================================================
  const [bindings, setBindings] = useState<any[]>([]);
  const [requirements, setRequirements] = useState<any[]>([]);
  const [scriptIdInput, setScriptIdInput] = useState<number | undefined>();
  const [bindingsLoading, setBindingsLoading] = useState(false);

  // ============================================================
  // 数据加载 - 身份注册
  // ============================================================
  const loadIdentities = useCallback(async () => {
    setIdentityLoading(true);
    try {
      const result = await getIdentityList({
        page: identityPage,
        page_size: identityPageSize,
        entity_type: entityTypeFilter,
        status: statusFilter,
        search: searchKeyword || undefined,
      });
      setIdentities(result.items);
      setIdentityTotal(result.total);
    } catch (error: any) {
      message.error(error.message || '加载 NFC 身份列表失败');
    } finally {
      setIdentityLoading(false);
    }
  }, [identityPage, identityPageSize, entityTypeFilter, statusFilter, searchKeyword]);

  useEffect(() => {
    loadIdentities();
  }, [loadIdentities]);

  // ============================================================
  // 数据加载 - 扫描日志
  // ============================================================
  const loadScanLogs = useCallback(async () => {
    setScanLogLoading(true);
    try {
      const result = await getScanLogList({
        page: scanLogPage,
        page_size: scanLogPageSize,
        world_id: worldIdFilter,
        result: scanResultFilter,
      });
      setScanLogs(result.items);
      setScanLogTotal(result.total);
    } catch (error: any) {
      message.error(error.message || '加载扫描日志列表失败');
    } finally {
      setScanLogLoading(false);
    }
  }, [scanLogPage, scanLogPageSize, worldIdFilter, scanResultFilter]);

  const loadStatistics = useCallback(async () => {
    try {
      const stats = await getScanStatistics({});
      setStatistics(stats);
    } catch (error: any) {
      console.error('加载扫描统计失败:', error);
    }
  }, []);

  useEffect(() => {
    loadScanLogs();
    loadStatistics();
  }, [loadScanLogs, loadStatistics]);

  // ============================================================
  // 数据加载 - 绑定规则
  // ============================================================
  const loadBindingsAndRequirements = useCallback(async () => {
    if (!scriptIdInput) {
      message.warning('请输入剧本 ID');
      return;
    }
    setBindingsLoading(true);
    try {
      const [bindingsResult, requirementsResult] = await Promise.all([
        getNfcBindings({ script_id: scriptIdInput }),
        getNfcRequirements({ script_id: scriptIdInput }),
      ]);
      setBindings(bindingsResult.bindings || []);
      setRequirements(requirementsResult.requirements || []);
    } catch (error: any) {
      message.error(error.message || '加载绑定规则失败');
    } finally {
      setBindingsLoading(false);
    }
  }, [scriptIdInput]);

  // ============================================================
  // 身份注册操作
  // ============================================================
  const handleCreateIdentity = () => {
    setEditingIdentity(null);
    identityForm.resetFields();
    setIdentityModalVisible(true);
  };

  const handleEditIdentity = (record: NfcIdentityRegistry) => {
    setEditingIdentity(record);
    identityForm.setFieldsValue({
      entity_type: record.entity_type,
      entity_id: record.entity_id,
      display_name: record.display_name,
      identity_desc: record.identity_desc,
      source: record.source,
      status: record.status,
      extra_metadata: record.extra_metadata,
    });
    setIdentityModalVisible(true);
  };

  const handleDeleteIdentity = async (id: number) => {
    try {
      await deleteIdentity(id);
      message.success('删除成功');
      loadIdentities();
    } catch (error: any) {
      message.error(error.message || '删除失败');
    }
  };

  const handleRetireIdentity = async (id: number) => {
    try {
      await retireIdentity(id);
      message.success('停用成功');
      loadIdentities();
    } catch (error: any) {
      message.error(error.message || '停用失败');
    }
  };

  const handleRestoreIdentity = async (id: number) => {
    try {
      await restoreIdentity(id);
      message.success('恢复成功');
      loadIdentities();
    } catch (error: any) {
      message.error(error.message || '恢复失败');
    }
  };

  const handleIdentitySubmit = async () => {
    try {
      const values = await identityForm.validateFields();
      if (editingIdentity) {
        await updateIdentity(editingIdentity.id, values as NfcIdentityRegistryUpdate);
        message.success('更新成功');
      } else {
        await createIdentity(values as NfcIdentityRegistryCreate);
        message.success('创建成功');
      }
      setIdentityModalVisible(false);
      loadIdentities();
    } catch (error: any) {
      if (error.errorFields) {
        return; // 表单验证失败
      }
      message.error(error.message || '操作失败');
    }
  };

  // ============================================================
  // 扫描日志操作
  // ============================================================
  const handleCreateScanLog = () => {
    scanLogForm.resetFields();
    scanLogForm.setFieldsValue({
      source: 'cloud_api',
      scanned_at: new Date().toISOString(),
    });
    setScanLogModalVisible(true);
  };

  const handleScanLogSubmit = async () => {
    try {
      const values = await scanLogForm.validateFields();
      await createScanLog(values);
      message.success('创建成功');
      setScanLogModalVisible(false);
      loadScanLogs();
      loadStatistics();
    } catch (error: any) {
      if (error.errorFields) {
        return;
      }
      message.error(error.message || '创建失败');
    }
  };

  // ============================================================
  // 表格列定义 - 身份注册
  // ============================================================
  const identityColumns: ColumnsType<NfcIdentityRegistry> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '实体类型',
      dataIndex: 'entity_type',
      key: 'entity_type',
      width: 120,
      render: (type: EntityType) => ENTITY_TYPE_LABELS[type],
      filters: Object.entries(ENTITY_TYPE_LABELS).map(([value, text]) => ({ text, value })),
    },
    {
      title: '标签内容 (entity_id)',
      dataIndex: 'entity_id',
      key: 'entity_id',
      width: 200,
      ellipsis: true,
    },
    {
      title: '显示名称',
      dataIndex: 'display_name',
      key: 'display_name',
      width: 150,
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (source: NfcSource) => NFC_SOURCE_LABELS[source],
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: NfcStatus) => (
        <Tag color={status === 'active' ? 'success' : 'default'}>
          {NFC_STATUS_LABELS[status]}
        </Tag>
      ),
      filters: Object.entries(NFC_STATUS_LABELS).map(([value, text]) => ({ text, value })),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date) => new Date(date).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEditIdentity(record)}>
            编辑
          </Button>
          {record.status === 'active' ? (
            <Button type="link" size="small" onClick={() => handleRetireIdentity(record.id)}>
              停用
            </Button>
          ) : (
            <Button type="link" size="small" onClick={() => handleRestoreIdentity(record.id)}>
              恢复
            </Button>
          )}
          <Popconfirm
            title="确认删除此身份？"
            onConfirm={() => handleDeleteIdentity(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ============================================================
  // 表格列定义 - 扫描日志
  // ============================================================
  const scanLogColumns: ColumnsType<NfcScanLog> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '世界 ID',
      dataIndex: 'world_id',
      key: 'world_id',
      width: 120,
      ellipsis: true,
    },
    {
      title: '玩家 ID',
      dataIndex: 'player_id',
      key: 'player_id',
      width: 120,
      ellipsis: true,
    },
    {
      title: '实体类型',
      dataIndex: 'entity_type',
      key: 'entity_type',
      width: 100,
      render: (type: EntityType) => ENTITY_TYPE_LABELS[type],
    },
    {
      title: '标签内容',
      dataIndex: 'entity_id',
      key: 'entity_id',
      width: 150,
      ellipsis: true,
    },
    {
      title: '扫描结果',
      dataIndex: 'result',
      key: 'result',
      width: 80,
      render: (result: ScanResult) => {
        const colorMap: Record<ScanResult, string> = {
          success: 'success',
          miss: 'warning',
          rejected: 'error',
          error: 'error',
        };
        return <Tag color={colorMap[result]}>{SCAN_RESULT_LABELS[result]}</Tag>;
      },
      filters: Object.entries(SCAN_RESULT_LABELS).map(([value, text]) => ({ text, value })),
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (source: ScanSource) => SCAN_SOURCE_LABELS[source],
    },
    {
      title: '扫描时间',
      dataIndex: 'scanned_at',
      key: 'scanned_at',
      width: 180,
      render: (date) => new Date(date).toLocaleString('zh-CN'),
    },
  ];

  // ============================================================
  // 渲染
  // ============================================================
  const tabItems = [
    {
      key: 'registry',
      label: (
        <span>
          <RobotOutlined />
          NFC 身份注册
        </span>
      ),
      children: (
        <>
          <div className="mb-4 flex justify-between">
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateIdentity}>
                创建身份
              </Button>
              <Input.Search
                placeholder="搜索 entity_id 或 display_name"
                allowClear
                style={{ width: 250 }}
                onSearch={(value) => setSearchKeyword(value)}
              />
              <Select
                placeholder="实体类型"
                allowClear
                style={{ width: 120 }}
                value={entityTypeFilter}
                onChange={(value) => setEntityTypeFilter(value)}
              >
                <Option value="slot">场馆固定槽位</Option>
                <Option value="figure">玩家人偶</Option>
                <Option value="card">玩家卡片</Option>
                <Option value="prop">可移动道具</Option>
              </Select>
              <Select
                placeholder="状态"
                allowClear
                style={{ width: 100 }}
                value={statusFilter}
                onChange={(value) => setStatusFilter(value)}
              >
                <Option value="active">激活</Option>
                <Option value="retired">停用</Option>
              </Select>
            </Space>
            <Button icon={<ReloadOutlined />} onClick={loadIdentities}>
              刷新
            </Button>
          </div>
          <Table
            columns={identityColumns}
            dataSource={identities}
            rowKey="id"
            loading={identityLoading}
            pagination={{
              current: identityPage,
              pageSize: identityPageSize,
              total: identityTotal,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => {
                setIdentityPage(page);
                setIdentityPageSize(pageSize);
              },
            }}
            scroll={{ x: 1200 }}
          />
        </>
      ),
    },
    {
      key: 'scan-logs',
      label: (
        <span>
          <ScanOutlined />
          扫描日志
        </span>
      ),
      children: (
        <>
          {statistics && (
            <Row gutter={16} className="mb-4">
              <Col span={4}>
                <Card>
                  <Statistic title="总扫描次数" value={statistics.total} />
                </Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic title="成功" value={statistics.success_count} valueStyle={{ color: '#3f8600' }} />
                </Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic title="未匹配" value={statistics.miss_count} valueStyle={{ color: '#faad14' }} />
                </Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic title="拒绝" value={statistics.rejected_count} valueStyle={{ color: '#cf1322' }} />
                </Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic title="错误" value={statistics.error_count} valueStyle={{ color: '#cf1322' }} />
                </Card>
              </Col>
              <Col span={4}>
                <Card>
                  <Statistic
                    title="成功率"
                    value={statistics.success_rate * 100}
                    suffix="%"
                    precision={2}
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
            </Row>
          )}
          <div className="mb-4 flex justify-between">
            <Space>
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreateScanLog}>
                创建扫描日志
              </Button>
              <Input placeholder="世界 ID" allowClear style={{ width: 150 }} onChange={(e) => setWorldIdFilter(e.target.value || undefined)} />
              <Select
                placeholder="扫描结果"
                allowClear
                style={{ width: 100 }}
                value={scanResultFilter}
                onChange={(value) => setScanResultFilter(value)}
              >
                <Option value="success">成功</Option>
                <Option value="miss">未匹配</Option>
                <Option value="rejected">拒绝</Option>
                <Option value="error">错误</Option>
              </Select>
            </Space>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadScanLogs}>
                刷新
              </Button>
              <Button icon={<SyncOutlined />} onClick={loadStatistics}>
                更新统计
              </Button>
            </Space>
          </div>
          <Table
            columns={scanLogColumns}
            dataSource={scanLogs}
            rowKey="id"
            loading={scanLogLoading}
            pagination={{
              current: scanLogPage,
              pageSize: scanLogPageSize,
              total: scanLogTotal,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, pageSize) => {
                setScanLogPage(page);
                setScanLogPageSize(pageSize);
              },
            }}
            scroll={{ x: 1200 }}
          />
        </>
      ),
    },
    {
      key: 'bindings',
      label: (
        <span>
          <RobotOutlined />
          绑定规则
        </span>
      ),
      children: (
        <>
          <div className="mb-4">
            <Space>
              <InputNumber
                placeholder="剧本 ID"
                value={scriptIdInput}
                onChange={(value) => setScriptIdInput(value || undefined)}
                style={{ width: 150 }}
              />
              <Button type="primary" icon={<SearchOutlined />} onClick={loadBindingsAndRequirements} loading={bindingsLoading}>
                查询
              </Button>
            </Space>
          </div>
          <Divider orientation="left">NFC 绑定规则</Divider>
          <Table
            columns={[
              { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
              { title: '配置键', dataIndex: 'config_key', key: 'config_key', width: 150 },
              { title: '配置名称', dataIndex: 'config_name', key: 'config_name', width: 200 },
              { title: '排序', dataIndex: 'sort_order', key: 'sort_order', width: 80 },
              {
                title: '配置数据',
                dataIndex: 'config_data',
                key: 'config_data',
                render: (data: any) => <pre>{JSON.stringify(data, null, 2)}</pre>,
              },
            ]}
            dataSource={bindings}
            rowKey="id"
            loading={bindingsLoading}
            pagination={false}
          />
          <Divider orientation="left">NFC 物料清单</Divider>
          <Table
            columns={[
              { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
              { title: '配置键', dataIndex: 'config_key', key: 'config_key', width: 150 },
              { title: '配置名称', dataIndex: 'config_name', key: 'config_name', width: 200 },
              { title: '排序', dataIndex: 'sort_order', key: 'sort_order', width: 80 },
              {
                title: '配置数据',
                dataIndex: 'config_data',
                key: 'config_data',
                render: (data: any) => <pre>{JSON.stringify(data, null, 2)}</pre>,
              },
            ]}
            dataSource={requirements}
            rowKey="id"
            loading={bindingsLoading}
            pagination={false}
          />
        </>
      ),
    },
  ];

  return (
    <div className="p-4">
      <Breadcrumb
        items={[
          { title: '内容管理' },
          { title: 'NFC 管理' },
        ]}
        className="mb-4"
      />
      <Tabs defaultActiveKey="registry" items={tabItems} />

      {/* 创建/编辑身份模态框 */}
      <Modal
        title={editingIdentity ? '编辑 NFC 身份' : '创建 NFC 身份'}
        open={identityModalVisible}
        onOk={handleIdentitySubmit}
        onCancel={() => setIdentityModalVisible(false)}
        width={600}
        okText="确认"
        cancelText="取消"
      >
        <Form form={identityForm} layout="vertical">
          <Form.Item
            name="entity_type"
            label="实体类型"
            rules={[{ required: true, message: '请选择实体类型' }]}
          >
            <Select placeholder="请选择实体类型">
              <Option value="slot">场馆固定槽位</Option>
              <Option value="figure">玩家人偶</Option>
              <Option value="card">玩家卡片</Option>
              <Option value="prop">可移动道具</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="entity_id"
            label="标签内容 (entity_id)"
            rules={[
              { required: true, message: '请输入标签内容' },
              { max: 255, message: '标签内容不能超过 255 字符' },
            ]}
            tooltip="NFC 标签芯片内容，支持中文、特殊字符、UTF-8 全字符集。自动去除首尾空格。"
          >
            <Input placeholder="请输入 NFC 标签内容" disabled={!!editingIdentity} />
          </Form.Item>
          <Form.Item
            name="display_name"
            label="显示名称"
            rules={[
              { required: true, message: '请输入显示名称' },
              { max: 255, message: '显示名称不能超过 255 字符' },
            ]}
          >
            <Input placeholder="请输入显示名称" />
          </Form.Item>
          <Form.Item name="identity_desc" label="描述">
            <TextArea placeholder="请输入描述（支持 HTML）" rows={3} />
          </Form.Item>
          <Form.Item name="source" label="来源" initialValue="venue">
            <Select>
              <Option value="venue">场馆</Option>
              <Option value="merch">商品发行</Option>
              <Option value="ops">运营</Option>
            </Select>
          </Form.Item>
          {editingIdentity && (
            <Form.Item name="status" label="状态" initialValue="active">
              <Select>
                <Option value="active">激活</Option>
                <Option value="retired">停用</Option>
              </Select>
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* 创建扫描日志模态框 */}
      <Modal
        title="创建扫描日志"
        open={scanLogModalVisible}
        onOk={handleScanLogSubmit}
        onCancel={() => setScanLogModalVisible(false)}
        width={600}
        okText="确认"
        cancelText="取消"
      >
        <Form form={scanLogForm} layout="vertical">
          <Form.Item
            name="world_id"
            label="世界实例 ID"
            rules={[{ required: true, message: '请输入世界实例 ID' }]}
          >
            <Input placeholder="请输入世界实例 ID" />
          </Form.Item>
          <Form.Item
            name="player_id"
            label="玩家 ID"
            rules={[{ required: true, message: '请输入玩家 ID' }]}
          >
            <Input placeholder="请输入玩家 ID" />
          </Form.Item>
          <Form.Item
            name="entity_type"
            label="实体类型"
            rules={[{ required: true, message: '请选择实体类型' }]}
          >
            <Select placeholder="请选择实体类型">
              <Option value="slot">场馆固定槽位</Option>
              <Option value="figure">玩家人偶</Option>
              <Option value="card">玩家卡片</Option>
              <Option value="prop">可移动道具</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="entity_id"
            label="标签内容"
            rules={[{ required: true, message: '请输入标签内容' }]}
          >
            <Input placeholder="请输入 NFC 标签内容" />
          </Form.Item>
          <Form.Item
            name="result"
            label="扫描结果"
            rules={[{ required: true, message: '请选择扫描结果' }]}
          >
            <Select placeholder="请选择扫描结果">
              <Option value="success">成功</Option>
              <Option value="miss">未匹配</Option>
              <Option value="rejected">拒绝</Option>
              <Option value="error">错误</Option>
            </Select>
          </Form.Item>
          <Form.Item name="device_id" label="设备 ID">
            <Input placeholder="请输入设备 ID（可选）" />
          </Form.Item>
          <Form.Item name="matched_binding_id" label="匹配的绑定规则 ID">
            <InputNumber placeholder="请输入绑定规则 ID（可选）" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="reason" label="失败原因">
            <TextArea placeholder="请输入失败原因（可选）" rows={2} />
          </Form.Item>
          <Form.Item name="source" label="来源" initialValue="cloud_api" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// 缺少的图标引入
const SearchOutlined = () => <span>查询</span>;

export default NfcManagement;
